// Spatial alignment model (F-03, D-37, D-38).
// B pixel (bx, by) lands at A position (dx + bx*sx, dy + by*sy). All coordinates in A pixels (D-33).

import type { AlignMode, Point, SpatialTransform } from './types';

export interface Size {
  w: number;
  h: number;
}

export const PRESET_MODES: readonly Exclude<AlignMode, 'manual'>[] = ['center', 'stretch', 'fit', 'topleft'];

/**
 * Integer upscale factor of B relative to A: 2..4 when B is roughly an integer multiple (≥ ~2x), else 1 (F-03.5).
 */
export function integerScaleOf(a: Size, b: Size): 1 | 2 | 3 | 4 {
  const r = Math.min(b.w / a.w, b.h / a.h);
  if (r < 1.8) return 1;
  const k = Math.round(r);
  if (k < 2) return 1;
  return Math.min(4, k) as 2 | 3 | 4;
}

/** Comparison grid multiplier: the largest integer factor among the B's (D-37). */
export function compareScaleOf(a: Size, bs: Size[]): 1 | 2 | 3 | 4 {
  let k: 1 | 2 | 3 | 4 = 1;
  for (const b of bs) {
    const f = integerScaleOf(a, b);
    if (f > k) k = f;
  }
  return k;
}

export function presetTransform(mode: Exclude<AlignMode, 'manual'>, a: Size, b: Size): SpatialTransform {
  const k = integerScaleOf(a, b);
  const unit = 1 / k;
  let sx: number;
  let sy: number;
  let dx: number;
  let dy: number;
  switch (mode) {
    case 'center':
      sx = sy = unit;
      dx = (a.w - b.w * sx) / 2;
      dy = (a.h - b.h * sy) / 2;
      break;
    case 'topleft':
      sx = sy = unit;
      dx = 0;
      dy = 0;
      break;
    case 'stretch':
      sx = a.w / b.w;
      sy = a.h / b.h;
      dx = 0;
      dy = 0;
      break;
    case 'fit': {
      const s = Math.min(a.w / b.w, a.h / b.h);
      sx = sy = s;
      dx = (a.w - b.w * s) / 2;
      dy = (a.h - b.h * s) / 2;
      break;
    }
  }
  return { mode, dx, dy, sx, sy, appliedFromSuggestion: false };
}

export const IDENTITY: SpatialTransform = { mode: 'center', dx: 0, dy: 0, sx: 1, sy: 1, appliedFromSuggestion: false };

/** Default transform when a B is loaded: centre without scaling (D-38); equal sizes give identity. */
export function defaultTransform(a: Size, b: Size): SpatialTransform {
  return presetTransform('center', a, b);
}

export function bToA(t: SpatialTransform, p: Point): Point {
  return { x: t.dx + p.x * t.sx, y: t.dy + p.y * t.sy };
}

export function aToB(t: SpatialTransform, p: Point): Point {
  return { x: (p.x - t.dx) / t.sx, y: (p.y - t.dy) / t.sy };
}

/** Smallest distance (pixels) between the two points along an axis for that axis' scale to be measured. */
export const POINT_MIN_SPAN = 8;

export interface PointsTransform {
  dx: number;
  dy: number;
  sx: number;
  sy: number;
  /** The axis whose scale could not be measured (points almost level / above each other) and took the other's. */
  borrowed: 'x' | 'y' | null;
}

/**
 * Two-point alignment (F-03.11): the same two places marked on A (A pixels) and on B (B pixels), in the same
 * order, give B's scale and position per axis, so a picture that was scaled, stretched, cropped or padded lines
 * up. An axis along which the points are too close takes the other axis' scale. Null when neither axis can be
 * measured or the order differs (a negative scale).
 */
export function transformFromPoints(a: readonly [Point, Point], b: readonly [Point, Point], minSpan = POINT_MIN_SPAN): PointsTransform | null {
  const axis = (k: 'x' | 'y'): number | null => {
    const da = a[1][k] - a[0][k];
    const db = b[1][k] - b[0][k];
    return Math.abs(da) >= minSpan && Math.abs(db) >= minSpan ? da / db : null;
  };
  let sx = axis('x');
  let sy = axis('y');
  let borrowed: 'x' | 'y' | null = null;
  if (sx === null && sy === null) return null;
  if (sx === null) [sx, borrowed] = [sy!, 'x'];
  if (sy === null) [sy, borrowed] = [sx, 'y'];
  if (!(sx > 0 && sy > 0)) return null;
  // Position: both points weigh the same (exact when the axis was measured).
  const dx = (a[0].x - b[0].x * sx + a[1].x - b[1].x * sx) / 2;
  const dy = (a[0].y - b[0].y * sy + a[1].y - b[1].y * sy) / 2;
  return { dx, dy, sx, sy, borrowed };
}

export interface CoverageInfo {
  /** A-pixel margins not covered by B (shown black): positive means black border. */
  blackLeft: number;
  blackTop: number;
  blackRight: number;
  blackBottom: number;
  /** B pixels cropped away because they fall outside A. */
  cropLeft: number;
  cropTop: number;
  cropRight: number;
  cropBottom: number;
  scaled: boolean;
}

export function coverageInfo(t: SpatialTransform, a: Size, b: Size): CoverageInfo {
  const left = t.dx;
  const top = t.dy;
  const right = t.dx + b.w * t.sx;
  const bottom = t.dy + b.h * t.sy;
  const r = (v: number) => Math.round(v * 10) / 10;
  return {
    blackLeft: r(Math.max(0, left)),
    blackTop: r(Math.max(0, top)),
    blackRight: r(Math.max(0, a.w - right)),
    blackBottom: r(Math.max(0, a.h - bottom)),
    cropLeft: r(Math.max(0, -left / t.sx)),
    cropTop: r(Math.max(0, -top / t.sy)),
    cropRight: r(Math.max(0, (right - a.w) / t.sx)),
    cropBottom: r(Math.max(0, (bottom - a.h) / t.sy)),
    scaled: Math.abs(t.sx - 1) > 1e-6 || Math.abs(t.sy - 1) > 1e-6,
  };
}

export function sameSize(a: Size, b: Size): boolean {
  return a.w === b.w && a.h === b.h;
}

export function transformsEqual(x: SpatialTransform, y: SpatialTransform, eps = 1e-6): boolean {
  return (
    Math.abs(x.dx - y.dx) < eps &&
    Math.abs(x.dy - y.dy) < eps &&
    Math.abs(x.sx - y.sx) < eps &&
    Math.abs(x.sy - y.sy) < eps
  );
}

/** Summary like "1988×1088 · stretch · X 96.58% Y 99.26%" (F-03.4); mode label is supplied by the caller (i18n). */
export function transformSummary(t: SpatialTransform, b: Size, modeLabel: string): string {
  const pct = (v: number) => `${(v * 100).toFixed(2)}%`;
  const parts = [`${b.w}×${b.h}`, modeLabel];
  if (Math.abs(t.sx - 1) > 1e-6 || Math.abs(t.sy - 1) > 1e-6) {
    parts.push(Math.abs(t.sx - t.sy) < 1e-6 ? pct(t.sx) : `X ${pct(t.sx)} Y ${pct(t.sy)}`);
  }
  if (Math.abs(t.dx) > 0.05 || Math.abs(t.dy) > 0.05) parts.push(`${fmtSigned(t.dx)}, ${fmtSigned(t.dy)} px`);
  return parts.join(' · ');
}

function fmtSigned(v: number): string {
  const s = (Math.round(v * 10) / 10).toString();
  return v > 0 ? `+${s}` : s;
}

/**
 * Scores preset candidates on downscaled luma images (F-03.8). `a` and `b` are grayscale images of size
 * (aw', ah') and (bw', bh') at the same downscale factor f relative to their originals. Scoring uses the
 * mean absolute difference over the central region (or a weight mask when provided).
 */
export interface GrayImage {
  w: number;
  h: number;
  data: Float32Array | Uint8Array | Uint8ClampedArray;
}

export function sampleBilinear(img: GrayImage, x: number, y: number): number {
  if (x < 0 || y < 0 || x > img.w - 1 || y > img.h - 1) return NaN;
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(img.w - 1, x0 + 1);
  const y1 = Math.min(img.h - 1, y0 + 1);
  const fx = x - x0;
  const fy = y - y0;
  const d = img.data;
  const top = d[y0 * img.w + x0] * (1 - fx) + d[y0 * img.w + x1] * fx;
  const bot = d[y1 * img.w + x0] * (1 - fx) + d[y1 * img.w + x1] * fx;
  return top * (1 - fy) + bot * fy;
}

/**
 * Mean absolute difference between A and B under transform t (expressed in full-resolution pixels),
 * evaluated on A's downscaled grid. scaleA/scaleB convert full-res pixels to the downscaled images.
 */
export function alignmentError(
  a: GrayImage,
  b: GrayImage,
  t: SpatialTransform,
  scaleA: number,
  scaleB: number,
  weight?: GrayImage,
  centralFraction = 0.6,
): number {
  const x0 = Math.floor((a.w * (1 - centralFraction)) / 2);
  const y0 = Math.floor((a.h * (1 - centralFraction)) / 2);
  const x1 = a.w - x0;
  const y1 = a.h - y0;
  let sum = 0;
  let wsum = 0;
  const step = 1;
  for (let y = y0; y < y1; y += step) {
    for (let x = x0; x < x1; x += step) {
      const w = weight ? weight.data[y * weight.w + x] / 255 : 1;
      if (w <= 0) continue;
      // Pixel centre in full-res A coordinates.
      const ax = (x + 0.5) / scaleA;
      const ay = (y + 0.5) / scaleA;
      const bx = (ax - t.dx) / t.sx;
      const by = (ay - t.dy) / t.sy;
      const bv = sampleBilinear(b, bx * scaleB - 0.5, by * scaleB - 0.5);
      if (Number.isNaN(bv)) {
        sum += 255 * w;
        wsum += w;
        continue;
      }
      sum += Math.abs(a.data[y * a.w + x] - bv) * w;
      wsum += w;
    }
  }
  return wsum > 0 ? sum / wsum : Infinity;
}

export interface AlignmentSuggestion {
  transform: SpatialTransform;
  error: number;
  scores: { mode: Exclude<AlignMode, 'manual'>; error: number }[];
  /** True for the arbitrary-scale estimate (F-03.2). */
  free?: boolean;
}

/**
 * Arbitrary shift + independent X/Y scale estimate (F-03.2): pattern search on (dx, dy, sx, sy) starting from the
 * best preset, minimising the same central-region error. Deterministic, no randomness.
 */
export function estimateFreeTransform(
  a: GrayImage,
  b: GrayImage,
  aFull: Size,
  bFull: Size,
  weight?: GrayImage,
): AlignmentSuggestion {
  const base = suggestAlignment(a, b, aFull, bFull, weight, 3);
  const scaleA = a.w / aFull.w;
  const scaleB = b.w / bFull.w;
  let x = [base.transform.dx, base.transform.dy, base.transform.sx, base.transform.sy];
  const err = (v: number[]) =>
    v[2] <= 0 || v[3] <= 0
      ? Infinity
      : alignmentError(a, b, { mode: 'manual', dx: v[0], dy: v[1], sx: v[2], sy: v[3], appliedFromSuggestion: true }, scaleA, scaleB, weight);
  let best = err(x);
  // Step sizes in full-resolution A pixels for shifts and relative units for scales.
  let steps = [4, 4, 0.01 * x[2], 0.01 * x[3]];
  const minSteps = [0.1, 0.1, 0.0002 * x[2], 0.0002 * x[3]];
  for (let iter = 0; iter < 400; iter++) {
    let improved = false;
    for (let i = 0; i < 4; i++) {
      for (const dir of [1, -1]) {
        const y = x.slice();
        y[i] += dir * steps[i];
        // Scaling about the image centre keeps the shift estimate stable while the scale changes.
        if (i >= 2) {
          const ax = i === 2 ? 0 : 1;
          const bsz = ax === 0 ? bFull.w : bFull.h;
          y[ax] -= (dir * steps[i] * bsz) / 2;
        }
        const e = err(y);
        if (e < best - 1e-9) {
          best = e;
          x = y;
          improved = true;
        }
      }
    }
    if (!improved) {
      let done = true;
      steps = steps.map((s, i) => {
        const n = s / 2;
        if (n >= minSteps[i]) done = false;
        return Math.max(n, minSteps[i]);
      });
      if (done) break;
    }
  }
  return {
    transform: { mode: 'manual', dx: x[0], dy: x[1], sx: x[2], sy: x[3], appliedFromSuggestion: true },
    error: best,
    scores: base.scores,
    free: true,
  };
}

/**
 * Suggests one of the four presets plus a ±range px offset refinement (F-03.8).
 * aFull/bFull are the original sizes; a/b are the downscaled gray images.
 */
export function suggestAlignment(
  a: GrayImage,
  b: GrayImage,
  aFull: Size,
  bFull: Size,
  weight?: GrayImage,
  refineRangePx = 3,
): AlignmentSuggestion {
  const scaleA = a.w / aFull.w;
  const scaleB = b.w / bFull.w;
  const scores = PRESET_MODES.map((mode) => ({
    mode,
    error: alignmentError(a, b, presetTransform(mode, aFull, bFull), scaleA, scaleB, weight),
  }));
  scores.sort((p, q) => p.error - q.error);
  const best = presetTransform(scores[0].mode, aFull, bFull);
  let bestT = best;
  let bestErr = scores[0].error;
  // Coarse-to-fine integer search, then 0.5 px refinement around the best integer offset.
  for (const stepPx of [1, 0.5]) {
    const range = stepPx === 1 ? refineRangePx : 0.5;
    const cx = bestT.dx;
    const cy = bestT.dy;
    for (let oy = -range; oy <= range + 1e-9; oy += stepPx) {
      for (let ox = -range; ox <= range + 1e-9; ox += stepPx) {
        if (ox === 0 && oy === 0) continue;
        const t = { ...best, dx: cx + ox, dy: cy + oy };
        const e = alignmentError(a, b, t, scaleA, scaleB, weight);
        if (e < bestErr - 1e-9) {
          bestErr = e;
          bestT = t;
        }
      }
    }
  }
  return { transform: { ...bestT, appliedFromSuggestion: true }, error: bestErr, scores };
}
